import TreeLine from './tree_line.js';

///////////////////////////////////////////////////////////////////////////////
// TREE EDITOR CLASS
//

let TreeEditor = new Class({
   Implements: Events,

   initialize: function (container, manager) {
      this.container = container;
      this.current = null;
      this.lineCounter = 1;
      this.maxLines = 8;
      this.lines = [];

      this.manager = manager;
      this.manager.register(this);

      this.container.getElements('.line').each(function (line) {
         // Track which line we're processing.
         let index = this.lineCounter++;

         // Create the new editable.
         let lineObj = new TreeLine(this, index, line, {
            errorHandler: this.manager.errorHandler,
         });

         // Add the new line to the list of lines.
         this.lines.push(lineObj);
      }, this);

      // store our original state so we can check for modifications
      this.original = this.serialize();
      this.modified = false;

      this.setupCopyLinesButton();
   },

   // LINE EDITING ////////////////////////////////////////////////////////////

   /**
    * Ends edit mode on `line` and begins edit mode on the next one, or does
    * nothing more if there are no more lines below.
    */
   editNextLine: function (line) {
      let nextLine = this.getNextLine(line);
      this.manager.endEditMode();

      if (nextLine) {
         this.manager.beginEditMode(this, nextLine);
      }
   },

   /**
    * Inserts a line below `line`, adding it as a child if `line` already has
    * children, and as a sibling otherwise.
    */
   insertLineBelow: function (line) {
      let newLine = this.createLine();
      let nextLine = this.getNextLine(line);

      // Mark the current line as "dirty" so that it won't be deleted when
      // leaving and moving to the next line. We do this so that the user may
      // create a sequence of new lines by hitting the '+' button repeatedly or
      // using the associated key command.
      line.setDirty(true);

      if (nextLine && nextLine.level > line.level) {
         newLine.setLevel(nextLine.level);
      } else {
         newLine.setLevel(line.level);
      }

      // Add the new line to the list of lines and the DOM.
      this.lines.splice(this.lines.indexOf(line) + 1, 0, newLine);
      newLine.inject(line, 'after');

      // Note that we've modified the step.
      this.updateModified();

      // Activate the new text paragraph and start editing.
      this.manager.beginEditMode(this, newLine);
   },

   /**
    * Ends edit mode on `line` and begins edit mode on the previous one, or
    * does nothing more if there are no more lines above.
    */
   editPreviousLine: function (line) {
      let prevLine = this.getPreviousLine(line);
      this.manager.endEditMode();

      if (prevLine !== null) {
         this.manager.beginEditMode(this, prevLine);
      }
   },

   /**
    * Inserts a line above `line`, always as `lines`'s left sibling.
    */
   insertLineAbove: function (line) {
      let newLine = this.createLine();
      newLine.setLevel(line.level);

      let index = this.lines.indexOf(line);
      this.lines.splice(index, 0, newLine);
      newLine.inject(line, 'before');

      if (line.isFresh()) {
         this.deleteLine(line);
      }

      // Note that we've modified the step.
      this.updateModified();

      // Activate the new text paragraph and start editing.
      this.manager.beginEditMode(this, newLine);
   },

   /**
    * Shifts `line` right if and only if it has an older sibling which can
    * become its parent, and indenting will not cause any line to be indented
    * more than two levels.
    */
   shiftLineRight: function (line) {
      // You can only shift right if you have an older sibling.
      if (this.getSiblingAtOffset(line, -1) === null) {
         throw new ApplicationError(_js('You cannot indent this bullet further right.'));
      }

      // The shift mustn't allow any line to have an indent > 2.
      if (this.getDeepestChildLevel(line) == 2) {
         throw new ApplicationError(_js('You can indent a maximum of two times.'));
      }

      // The indent on line, and all of its children increases.
      let i = this.lines.indexOf(line) + 1;
      for (; i < this.lines.length; i++) {
         let l = this.lines[i];
         if (l.level <= line.level) {
            break;
         }
         l.setLevel(l.level + 1);
      }
      line.setLevel(line.level + 1);

      // Note that we've modified the step.
      this.updateModified();
   },

   /**
    * Shifts `line` left if and only if it's not already as far left as it'll
    * go; adopts all of the lines below it so that they are indented one extra
    * level.
    */
   shiftLineLeft: function (line) {
      // You can only shift left if you're already indented.
      if (line.level == 0) {
         throw new ApplicationError(_js('You cannot move this bullet further left.'));
      }

      let index = this.lines.indexOf(line) + 1;
      for (; index < this.lines.length; index++) {
         let l = this.lines[index];
         if (l.level <= line.level) {
            break;
         } else {
            l.setLevel(l.level - 1);
         }
      }
      line.setLevel(line.level - 1);

      // Note that we've modified the step.
      this.updateModified();
   },

   /**
    * Deletes `line`, removing it from the list of lines and from the DOM.
    * After deleting the line, focus goes to the next or previous line, unless
    * the line is fresh or the doNotAdvance flag is true, in which case the
    * caller determines what happens next.
    */
   deleteLine: function (line, doNotAdvance) {
      // No deleting the very last line.
      if (this.lines.length == 1) {
         throw new ApplicationError(_js('You cannot delete the last line.'));
      }

      // No deleting a line with children.
      let nextLine = this.getNextLine(line);
      if (nextLine && nextLine.level > line.level) {
         throw new ApplicationError(_js('You cannot delete a line that has sublines.'));
      }

      // Get some information about the line before we delete it.
      let index = this.lines.indexOf(line);
      let deletingFresh = line.isFresh();
      let deletingFirst = this.isFirstLine(line);

      // Remove the line.
      this.lines.splice(index, 1);
      line.dispose();
      this.current = null;

      // Note that we've modified the step.
      this.updateModified();

      if (!doNotAdvance && !deletingFresh) {
         // Start editing the next line.
         const nextLineIndex = Math.min(this.lines.length - 1, index);
         this.manager.beginEditMode(this, this.lines[nextLineIndex]);
      }
   },

   /**
    * If the current line is fresh, it gets deleted. If there's a previous line
    * then we move to it, otherwise we insert a new line above so long as the
    * current line wasn't fresh.
    */
   editPreviousOrInsertAbove: function (line) {
      let prevLine = this.getPreviousLine(line);
      this.manager.endEditMode();

      if (prevLine) {
         this.manager.beginEditMode(this, prevLine);
      } else if (!line.isFresh()) {
         this.insertLineAbove(line);
      }
   },

   /**
    * If the current line is fresh, it gets deleted. If there's a next line
    * then we move to it, otherwise we insert a new line below so long as the
    * current line wasn't fresh.
    */
   editNextOrInsertBelow: function (line) {
      let next = this.getNextLine(line);
      this.manager.endEditMode();

      if (next) {
         this.manager.beginEditMode(this, next);
      } else if (!line.isFresh()) {
         this.insertLineBelow(line);
      }
   },

   // EDIT MODE TRANSITIONS ///////////////////////////////////////////////////

   /**
    * Deactivates all lines.
    */
   blurLines: function () {
      this.lines.invoke('deactivate');
   },

   /**
    * Replaces a rendered line with the editor (displaying the current wiki
    * text) and the bullet selector (displaying the current bullet), and gives
    * the text area focus.
    */
   beginEditMode: function (line) {
      // You can only edit one line at a time.
      if (this.current) {
         this.current.endEditMode();
      }

      line.beginEditMode();
      this.current = line;
   },

   /**
    * Stores the updated wiki text and (if it's different from the old text)
    * sends an AJAX request to get new rendered text; replaces the editor with
    * a "Rendering..." message.
    */
   endEditMode: function () {
      if (!this.current) {
         return;
      } else if (this.current.isFresh() || this.current.isEmpty()) {
         // Delete the line, passing the doNotAdvance flag to prevent the focus
         // from moving to the next line.
         this.deleteLine(this.current, true);
      } else {
         this.current.endEditMode();
         this.current = null;
         this.updateModified();
      }
   },

   updateModified: function () {
      this.modified = this.serialize() !== this.original;
      this.manager.updateModified();
   },

   // SAVING //////////////////////////////////////////////////////////////////

   /**
    * End edit mode (if we're currently editing a step), clear out any old
    * saved information, and start at the top
    */
   save: function () {
      this.manager.endEditMode();
      this.clearForm();
   },

   /**
    * Clears the form of any inputs hanging around, which can sometimes
    * (depending on the browser) occur when the user hits the back/forward
    * buttons after submitting the form.
    */
   clearForm: function () {
      this.container.getElements('input[type=hidden]').invoke('dispose');
   },

   // HELPERS /////////////////////////////////////////////////////////////////

   /**
    * Creates a new editable with a new <p> element, setting several defaults
    * for fields that lines use in addition to the basic editable fields. The
    * bullet type is set to 'standard', and the line gets the text 'New line.'.
    */
   createLine: function () {
      if (this.lines.length >= this.maxLines) {
         throw new ApplicationError(
            _js(
               "You're maxed out! Steps can only have eight lines so they look great in all formats, including mobile devices."
            )
         );
      }

      this.lineCounter++;

      return new TreeLine(this, this.lineCounter, null, {
         errorHandler: this.manager.errorHandler,
      });
   },

   /**
    * Returns true if the line comes first in the sequence, and false
    * otherwise.
    */
   isFirstLine: function (line) {
      return line == this.lines[0];
   },

   /**
    * Returns the next line in the linear bullet sequence, or null if `line` is
    * at the bottom.
    */
   getNextLine: function (line) {
      let index = this.lines.indexOf(line) + 1;
      return index < this.lines.length ? this.lines[index] : null;
   },

   /**
    * Returns the previous line in the linear bullet sequence, or null if
    * `line` is at the top.
    */
   getPreviousLine: function (line) {
      let index = this.lines.indexOf(line) - 1;
      return index >= 0 ? this.lines[index] : null;
   },

   /**
    * Returns the `offset`th sibling of `line`. Note that a sibling is at the
    * same level.
    */
   getSiblingAtOffset: function (line, offset) {
      let index = this.lines.indexOf(line) + offset;
      let next;

      while (index < this.lines.length && index >= 0) {
         next = this.lines[index];
         if (next.level < line.level) {
            // We're out of siblings.
            return null;
         } else if (next.level == line.level) {
            // We found a siblind.
            return next;
         } else {
            // We found the child of a sibling, so keep looking.
            index += offset;
         }
      }

      return null;
   },

   /**
    * Finds the level of the most-indented child of a line, including the line
    * itself.
    */
   getDeepestChildLevel: function (line) {
      let index = this.lines.indexOf(line) + 1;
      let level = line.level;

      for (; index < this.lines.length; index++) {
         let next = this.lines[index];
         if (next.level <= line.level) {
            return level;
         } else {
            level = Math.max(level, next.level);
         }
      }

      return level;
   },

   /**
    * Returns a string representation of the line tree.
    */
   serialize: function () {
      let str = '';
      this.lines.each(function (line) {
         str += line.serialize();
      });
      return str;
   },

   copyLines: function (renderedLines, inputText) {
      let currentLine = 0;
      this.lines.each(
         function (line) {
            line.textEl.set('html', renderedLines[currentLine]);

            let newSource = inputText[currentLine];
            line.inputEl.value = line.lastSource = line.source = newSource;

            currentLine++;
         }.bind(this)
      );

      $('saveStep').enable();
      $$('.js-copy-step-lines').set('disabled', 'disabled');
   },

   setupCopyLinesButton: function () {
      let params = [App.renderedLines, App.wikiTextLines];
      let copySourceStep = this.copyLines.pass(params, this);
      $$('.js-copy-step-lines').addEvent('click', copySourceStep);
   },
});

export default TreeEditor;
